// Battle setup: builds a ready-to-play World with starting forces, plus a scripted
// demonstration firefight that walks through the phases advance → pin → flank → break
// → charge (GDD M1 definition-of-done) for the "Watch Demo" mode and the smoke test.

import type { Order, WorldConfig } from "./types";
import { World } from "./world";

function order(o: Partial<Order>): Order {
  return { type: "stop", pos: null, facing: null, targetSquad: -1, targetPoint: -1, ...o };
}

/** Standard 1v1 skirmish: HQ + barracks + generator each, plus a small starting force. */
export function createBattle(cfg: WorldConfig): World {
  const w = new World(cfg);

  const lh = w.map.hq.legion;
  w.createSquad("legion", "legion_line", { x: lh.x + 150, y: lh.y - 60 });
  w.createSquad("legion", "legion_line", { x: lh.x + 150, y: lh.y + 60 });
  w.createSquad("legion", "legion_heavy", { x: lh.x + 120, y: lh.y });

  const kh = w.map.hq.kindled;
  w.createSquad("kindled", "kindled_zealot", { x: kh.x - 150, y: kh.y - 50 });
  w.createSquad("kindled", "kindled_zealot", { x: kh.x - 150, y: kh.y + 50 });
  w.createSquad("kindled", "kindled_firebearer", { x: kh.x - 120, y: kh.y });

  return w;
}

/**
 * Scripted demonstration firefight. A Legion firing line (with a suppression MG) holds
 * cover on the west of Cathedral Square; a Kindled assault force advances from the east.
 * The script issues a few timed orders; the systems then produce the readable phases:
 *   advance → the MG PINS the zealots → a fire-bearer FLANKS south → pinned zealots
 *   BREAK into FRENZY and CHARGE → the shock cult melee-locks the line.
 */
export function scriptedFirefight(w: World): void {
  // clear default forces (keep HQs/structures for morale support + income)
  for (const sq of w.squads) sq.dead = true;
  w.squads = [];
  w.modelOwner.clear();
  w.modelById.clear();

  const line1 = w.createSquad("legion", "legion_line", { x: 940, y: 700 });
  const line2 = w.createSquad("legion", "legion_line", { x: 940, y: 900 });
  const heavy = w.createSquad("legion", "legion_heavy", { x: 860, y: 800 });
  const assault = w.createSquad("legion", "legion_assault", { x: 760, y: 800 }, { sergeant: true });
  for (const s of [line1, line2, heavy, assault]) {
    s.facing = 0;
    s.order = order({ type: "hold", pos: { ...s.center }, facing: 0 });
  }

  const z1 = w.createSquad("kindled", "kindled_zealot", { x: 1520, y: 700 });
  const z2 = w.createSquad("kindled", "kindled_zealot", { x: 1520, y: 900 });
  const shock = w.createSquad("kindled", "kindled_shock", { x: 1580, y: 800 }, { sergeant: true });
  const fire = w.createSquad("kindled", "kindled_firebearer", { x: 1600, y: 660 });
  for (const s of [z1, z2, shock, fire]) s.facing = Math.PI;

  const done = { advance: false, flank: false, charge: false, relieve: false };
  w.onTick = (world) => {
    const t = world.time;
    const sq = (id: number) => world.squads.find((s) => s.id === id && !s.dead);
    if (!done.advance && t > 1.0) {
      done.advance = true;
      const a = sq(z1.id);
      const b = sq(z2.id);
      if (a) a.order = order({ type: "attackmove", pos: { x: 1120, y: 720 } });
      if (b) b.order = order({ type: "attackmove", pos: { x: 1120, y: 880 } });
    }
    if (!done.flank && t > 7.0) {
      done.flank = true;
      const f = sq(fire.id);
      if (f) f.order = order({ type: "attackmove", pos: { x: 1000, y: 1080 } });
    }
    if (!done.charge && t > 10.0) {
      done.charge = true;
      const s = sq(shock.id);
      if (s) s.order = order({ type: "attack", targetSquad: line2.id });
    }
    if (!done.relieve && t > 15.0) {
      done.relieve = true;
      const a = sq(assault.id);
      const target = a ? world.nearestEnemySquad("legion", a.center, 2000) : null;
      if (a && target) a.order = order({ type: "attack", targetSquad: target.id });
    }
  };
}
